import React, { useRef, useEffect, useState } from 'react'
import { m, motion } from 'framer-motion'
import { styled } from '@mui/material/styles'
import { Stack, Container, Typography, Card, Button, Grid } from '@mui/material'
import { varFade } from '../../components/animate'
import Image from 'src/components/image'

// ----------------------------------------------------------------------

const StyledRoot = styled('div')(({ theme }) => ({
  backgroundColor: '#fff',
  padding: theme.spacing(10, 0),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(5, 0),
    height: '60vh',
  },
  height: '60vh',
}))

const StyledContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  [theme.breakpoints.up('md')]: {
    height: '15vh', // 15vh on desktop,
  },
  height: '1vh', // 15vh on desktop,
  padding: 0,
}))

const SMART_FORCASTING_IMAGE = 'assets/background/services-carosel/Smart-Forecasting.png'
const AIR_CARGO_IMAGE = 'assets/background/services-carosel/AIR-Cargo.png'
const AUDITING_INVOICE_IMAGE = 'assets/background/services-carosel/Auditing-Invoice.png'
const BLUE_PORT_IMAGE = 'assets/background/services-carosel/Blue-Port.png'
const BOOKING_IMAGE = 'assets/background/services-carosel/Booking.png'
const CUSTOMS_COPY_IMAGE = 'assets/background/services-carosel/Customs-copy.png'
const DO_CREATION_IMAGE = 'assets/background/services-carosel/DO-Creation.png'
const DOCUMENTS_IMAGE = 'assets/background/services-carosel/Documents.png'
const ERP_IMAGE = 'assets/background/services-carosel/ERP.png'
const FORECASTING_IMAGE = 'assets/background/services-carosel/Forecasting.png'
const GLOBAL_MAP_IMAGE = 'assets/background/services-carosel/Global-map.png'
const MANAGEMENT_IMAGE = 'assets/background/services-carosel/Management.png'
const PLATFORM_IMAGE = 'assets/background/services-carosel/Platform.png'
const RAIL_CARGO_IMAGE = 'assets/background/services-carosel/Rail-Cargo.png'
const SCHEDULING_IMAGE = 'assets/background/services-carosel/Scheduling.png'
const TRANSLOAD_IMAGE = 'assets/background/services-carosel/Transload.png'
const WAREHOUSE_IMAGE = 'assets/background/services-carosel/Warehouse.png'

const data = [
  {
    name: 'Smart Forecasting',
    image: SMART_FORCASTING_IMAGE,
    link: '/services',
  },
  {
    name: 'Air Freight Tracking',
    image: AIR_CARGO_IMAGE,
    link: '/international',
  },
  {
    name: 'Rail Freight Tracking',
    image: RAIL_CARGO_IMAGE,
    link: '/shippers',
  },
  {
    name: 'Auditing Invoice',
    image: AUDITING_INVOICE_IMAGE,
    link: '/services',
  },
  {
    name: 'Demurrage Alarm',
    image: BLUE_PORT_IMAGE,
    link: '/services',
  },
  {
    name: 'Booking',
    image: BOOKING_IMAGE,
    link: '/services',
  },
  {
    name: 'Customs Copy',
    image: CUSTOMS_COPY_IMAGE,
    link: '/services',
  },
  {
    name: 'DO Creation',
    image: DO_CREATION_IMAGE,
    link: '/services',
  },
  {
    name: 'Documents',
    image: DOCUMENTS_IMAGE,
    link: '/services',
  },
  {
    name: 'ERP Integration',
    image: ERP_IMAGE,
    link: '/services',
  },
  {
    name: 'Inbound Forecasting',
    image: FORECASTING_IMAGE,
    link: '/services',
  },
  {
    name: 'Global Map',
    image: GLOBAL_MAP_IMAGE,
    link: '/international',
  },
  {
    name: 'Drayage Optimizer',
    image: MANAGEMENT_IMAGE,
    link: '/services',
  },
  {
    name: 'Custom Dashboards',
    image: PLATFORM_IMAGE,
    link: '/services',
  },
  {
    name: 'Cost Optimization',
    image: ERP_IMAGE,
    link: '/services',
  },
  {
    name: 'Rail Cargo',
    image: RAIL_CARGO_IMAGE,
    link: '/services',
  },
  {
    name: 'Scheduling Visibility',
    image: SCHEDULING_IMAGE,
    link: '/services',
  },
  {
    name: 'Booking Visibility',
    image: TRANSLOAD_IMAGE,
    link: '/services',
  },
  {
    name: 'Warehouse Management',
    image: WAREHOUSE_IMAGE,
    link: '/services',
  },
  {
    name: 'PO Management',
    image: MANAGEMENT_IMAGE,
    link: '/services',
  },
]

const firstHalfDataSet = data.slice(0, 10)
const secondHalfDataSet = data.slice(10, 20)

// ----------------------------------------------------------------------

export default function HomeServicesCarosel() {
  const carouselSpeed = 0.3 // Adjust the speed as needed
  const containerRef = useRef(null)
  const stackRef = useRef(null)
  const [offset, setOffset] = useState(0)
  const [offsetSecond, setOffsetSecond] = useState(0)

  useEffect(() => {
    let requestIdFirst
    let requestIdSecond

    const animateCarousel = () => {
      if (stackRef.current && containerRef.current) {
        const newOffset = (offset + carouselSpeed) % (stackRef.current.scrollWidth - containerRef.current.clientWidth)
        setOffset(newOffset)
      }
      requestIdFirst = requestAnimationFrame(animateCarousel)
    }

    const animateCarouselSecond = () => {
      if (stackRef.current && containerRef.current) {
        const newOffsetSecond = (offsetSecond + carouselSpeed) % (stackRef.current.scrollWidth - containerRef.current.clientWidth)
        setOffsetSecond(newOffsetSecond)
      }
      requestIdSecond = requestAnimationFrame(animateCarouselSecond)
    }

    animateCarousel()
    animateCarouselSecond()

    return () => {
      cancelAnimationFrame(requestIdFirst)
      cancelAnimationFrame(requestIdSecond)
    }
  }, [offset, offsetSecond])

  return (
    <StyledRoot>
      <StyledContainer component={m.div} ref={containerRef}>
        <Grid container spacing={2} sx={{ mt: 3, maxWidth: 1200, pb: -30 }}>
          <Grid item xs={12} md={12}>
            <Description />
          </Grid>

          <Grid item xs={12} md={12}>
            <Stack
              ref={stackRef}
              spacing={3}
              direction="row"
              justifyContent="center" // Align the icons in the center
              style={{ transform: `translateX(-${offset}px)` }}
            >
              {firstHalfDataSet.map((src, index) => (
                <motion.div
                  key={index}
                  custom={index} // Pass the index as a custom prop for stagger effect
                  initial={{ opacity: 0, x: -50 }} // Initial position and opacity for cards in the first row
                  animate={{ opacity: 1, x: 0 }} // Animation to move cards to their final position
                  exit={{ opacity: 0, x: 50 }} // Animation when cards are removed (for transition)
                  transition={{ duration: 0.5 }} // Stagger effect and animation duration
                >
                  <IntegrationCard name={src.name} image={src.image} link={src.link} />
                </motion.div>
              ))}
            </Stack>
          </Grid>
          <Grid item xs={12} md={12}>
            <Stack spacing={3} direction="row" justifyContent="center" style={{ transform: `translateX(${offsetSecond}px)` }}>
              {secondHalfDataSet.map((src, index) => (
                <motion.div
                  key={index}
                  custom={index} // Pass the index as a custom prop for stagger effect
                  initial={{ opacity: 0, x: 50 }} // Initial position and opacity for cards in the second row
                  animate={{ opacity: 1, x: 0 }} // Animation to move cards to their final position
                  exit={{ opacity: 0, x: -50 }} // Animation when cards are removed (for transition)
                  transition={{ delay: index * 0.1, duration: 0.5 }} // Stagger effect and animation duration
                >
                  <IntegrationCard name={src.name} image={src.image} link={src.link} />
                </motion.div>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </StyledContainer>
    </StyledRoot>
  )
}

// ----------------------------------------------------------------------

function Description() {
  return (
    <Stack spacing={3} sx={{ mt: 30, mb: 3, textAlign: 'center' }}>
      <m.div variants={varFade().inUp}>
        <Typography component="div" variant="overline" sx={{ mb: 0, color: 'text.disabled' }}>
          Discover
        </Typography>
      </m.div>

      <m.div variants={varFade().inDown}>
        <Typography variant="h2">CLM Platform</Typography>
      </m.div>
    </Stack>
  )
}
function IntegrationCard({ name, image, link }) {
  return (
    <motion.div>
      <Card
        sx={{
          maxWidth: 380,
          minWidth: 360,
          maxHeight: 110,
          backgroundColor: 'common.primary',
          display: 'flex',
          alignItems: 'center',
          padding: 2,
          borderRadius: 3,
          borderColor: 'common.black',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', // Adding a subtle shadow
          background: 'linear-gradient(20deg, #fbfbfd, #e9e9f2)', // Adjust gradient colors
          // Setup a space in between and align items left and right
          justifyContent: 'space-between',
        }}
      >
        <img
          src={image}
          alt="container tracking"
          style={{
            height: 'auto',
            width: 105,
            padding: '4px',
            marginLeft: '8px',
          }}
        />
        <Button
          size="medium"
          variant="contained"
          component="a"
          href={link}
          sx={{
            mr: 2,
            padding: '18px 32px',
            color: 'common.black',
            borderColor: 'transparent', // Set border color to transparent
            fontSize: '14px',
            fontWeight: 600,
            shapeOutside: 'border-box',
            borderRadius: '999px',
            background: 'linear-gradient(20deg, #fbfbfd, #e9e9f2)', // Adjust gradient colors
            '&:hover': {
              backgroundColor: 'transparent', // No background change on hover
              transition: 'transform 0.3s ease-in-out',
              color: 'common.primary', // Text color change on hover
              boxShadow: 'none', // No shadow on hover
            },
          }}
        >
          {name}
        </Button>
      </Card>
    </motion.div>
  )
}
